import React, { useEffect, useRef, useState } from "react";
import { Chess, Move, Piece, PieceSymbol, Square } from "chess.js";
// @ts-expect-error import error
import { Chessboard as ReactChessboard } from "react-chessboard";
import { useViewportSize } from "@mantine/hooks";
import { Mode, State, User } from "./models";
import { useSettingsStorage, useStorage } from "./ZustandState";
import _ from "lodash";
import { SIDE_PANEL_WIDTH } from "./constants";
import {
    IconChessBishopFilled,
    IconChessFilled,
    IconChessKnightFilled,
    IconChessQueenFilled,
    IconChessRookFilled,
} from "@tabler/icons-react";
import { getTakenMaterialDifference, MaterialDifferencePiece } from "./utils";
import cn from "classnames";

interface ChessboardProps {
    game: Chess;
    onMove: (move: Move) => void;
    playerColor: "w" | "b";
    result?: "success" | "fail";
    state: State;
    enablePremoves?: boolean;
    user?: User;
    mode?: Mode;
    setState?: (state: State) => void;
}

const BOARD_STYLES = {
    green: {
        label: "Green",
        darkSquareColor: "#779952",
        lightSquareColor: "#edeed1",
        lastMoveColor: "rgba(255, 255, 0, 0.4)",
        hoverColor: "rgba(255, 255, 0, 0.4)",
        arrowColor: undefined as string | undefined,
    },
    brown: {
        label: "Brown",
        darkSquareColor: "#B58863",
        lightSquareColor: "#F0D9B5",
        lastMoveColor: "rgba(155, 199, 0, .41)",
        hoverColor: "rgba(20, 85, 30, .5)",
        arrowColor: "#15781B" as string | undefined,
    },
    blue: {
        label: "Blue",
        darkSquareColor: "#86afcf",
        lightSquareColor: "#d8ecfb",
        lastMoveColor: "#83C9FE",
        hoverColor: "rgba(0, 200, 255, 0.4)",
        arrowColor: "#0070c0" as string | undefined,
    },
};

type ClearPremoves = {
    clearPremoves: (clearLastPieceColour?: boolean) => void;
};

const Chessboard: React.FC<ChessboardProps> = ({
    game,
    onMove,
    playerColor,
    result,
    state,
    enablePremoves,
    user,
    mode,
    setState,
}) => {
    const [dropSquareStyle, setDropSquareStyle] = useState({});
    const [squareStyles, setSquareStyles] = useState();
    const [pieceSquare, setPieceSquare] = useState<Square>();
    const [moveFrom, setMoveFrom] = useState<Square>();
    const chessboardRef = useRef<ClearPremoves>(null);
    const [optionSquares, setOptionSquares] = useState({});
    const [hoveredSquare, setHoveredSquare] = useState<Square>();
    const [squareInCheck, setSquareInCheck] = useState<Record<Square, any>>({});
    const [promotionMove, setPromotionMove] = useState();
    const [rightClickedSquares, setRightClickedSquares] = useState({});
    const setIllegalMoveInMenu = useStorage((state) => state.setIllegalMoveInMenu);
    const arrowToDraw = useStorage((state) => state.arrowToDraw);
    const settings = useSettingsStorage((state) => state);
    const evaluations = useStorage((state) => state.evaluations);
    const setEvaluations = useStorage((state) => state.setEvaluations);

    const boardColor = settings.boardColor;
    const boardStyle = BOARD_STYLES[boardColor] ?? BOARD_STYLES.green;
    const SQUARE_COLOR_LAST_MOVE = boardStyle.lastMoveColor;
    const SQUARE_COLOR_HOVER = boardStyle.hoverColor;

    const lastMove = game.history({ verbose: true }).slice(-1)[0];
    const lastMoveToCanBeCaptured = game
        .moves({ verbose: true })
        .some((m) => m.from === moveFrom && m.to === lastMove?.to);

    const lastMoveSquares = lastMove
        ? {
              [lastMove.from]: { backgroundColor: SQUARE_COLOR_LAST_MOVE },
              ...(!lastMoveToCanBeCaptured && { [lastMove.to]: { backgroundColor: SQUARE_COLOR_LAST_MOVE } }),
          }
        : {};

    const fen = game.fen();

    const getPiecePositions = (game: Chess, piece: Piece): Square[] => {
        return []
            .concat(...game.board())
            .map((p, index) => {
                if (p !== null && p.type === piece.type && p.color === piece.color) {
                    return index;
                }
            })
            .filter(Number.isInteger)
            .map((piece_index) => {
                const row = "abcdefgh"[piece_index % 8];
                const column = Math.ceil((64 - piece_index) / 8);
                return row + column;
            });
    };

    useEffect(() => {
        // New move/puzzle
        setSquareInCheck(undefined);

        if (game.inCheck() && game.turn() === playerColor) {
            const kingSquare = getPiecePositions(game, {
                type: "k",
                color: playerColor,
            })[0];
            setSquareInCheck({
                [kingSquare]: {
                    background:
                        "radial-gradient(ellipse at center, rgb(255, 0, 0) 0%, rgb(231, 0, 0) 25%, rgba(169, 0, 0, 0) 89%, rgba(158, 0, 0, 0) 100%)",
                },
            });
        }
    }, [fen]);

    useEffect(() => {
        // New puzzle or user clicks "retry"
        chessboardRef.current?.clearPremoves();
        resetHighlights();
        setSquareInCheck(undefined);
    }, [state]);

    useEffect(() => {
        if (!_.isEmpty(arrowToDraw) && arrowToDraw.shouldUndo) {
            game.undo();
            const keys = Object.keys(evaluations).map(Number);
            const maxKey = Math.max(...keys);
            const newEvaluations = { ...evaluations };
            delete newEvaluations[maxKey];
            setEvaluations(newEvaluations);
        }
    }, [arrowToDraw]);

    const onDrop = (sourceSquare: Square, targetSquare: Square, piece: any) => {
        if (state === "attempt_finished") {
            setIllegalMoveInMenu(true);

            return;
        }

        // see if the move is legal
        let move;
        try {
            move = game.move({
                from: sourceSquare,
                to: targetSquare,
                promotion: piece[1].toLowerCase() ?? "q", // always promote to a queen for example simplicity
            });
        } catch (error) {
            return;
        }

        // illegal move
        if (move === null) return false;
        onMove(move);

        resetHighlights();
        return true;
    };

    const resetHighlights = () => {
        setMoveFrom(undefined);
        setSquareStyles(undefined);
        setOptionSquares({});
        setRightClickedSquares({});
    };

    const onMouseOverSquare = (square: Square) => {
        if (square in optionSquares) {
            setSquareStyles(
                squareStyling({
                    pieceSquare: square,
                    history: game.history({ verbose: true }),
                    SQUARE_COLOR_HOVER,
                }),
            );
            setHoveredSquare(square);
        }
    };

    const onMouseOutSquare = () => {
        if (hoveredSquare) {
            setHoveredSquare(undefined);
            const newItems: any = { ...squareStyles };
            delete newItems[hoveredSquare];
            setSquareStyles(newItems);
        }
    };

    const getMoveOptions = (square: Square) => {
        const moves = game.moves({
            square,
            verbose: true,
        });
        if (moves.length === 0) {
            setOptionSquares({});
            return false;
        }

        const newSquares: any = {};
        moves.map((move) => {
            newSquares[move.to] = {
                background:
                    game.get(move.to) && game.get(move.to).color !== game.get(square).color
                        ? "radial-gradient(circle, rgba(0,0,0,.1) 85%, transparent 85%)"
                        : "radial-gradient(circle, rgba(0,0,0,.1) 25%, transparent 25%)",
                borderRadius: "50%",
            };
            return move;
        });
        newSquares[square] = {
            background: SQUARE_COLOR_HOVER,
        };
        setOptionSquares(newSquares);
        return true;
    };

    const onSquareClick = (square: Square) => {
        if (playerColor !== game.turn() && state === "playing") {
            return;
        }

        if (state === "attempt_finished") {
            return;
        }

        setRightClickedSquares({});
        const validFromSquares = game.moves({ verbose: true }).map((m) => m.from);

        if (moveFrom === square) {
            resetHighlights();
            return;
        }

        if (!moveFrom) {
            getMoveOptions(square);
        }

        if (!moveFrom) {
            setMoveFrom(square);
            setSquareStyles(
                squareStyling({
                    pieceSquare: square,
                    history: game.history({ verbose: true }),
                    SQUARE_COLOR_HOVER,
                }),
            );
            return;
        }

        const moves = game.moves({
            moveFrom: square,
            verbose: true,
        });

        const foundMove = moves.find((m) => m.from === moveFrom && m.to === square);

        if (foundMove) {
            // if promotion move
            if (
                (foundMove.color === "w" && foundMove.piece === "p" && square[1] === "8") ||
                (foundMove.color === "b" && foundMove.piece === "p" && square[1] === "1")
            ) {
                setPromotionMove(foundMove);
                return;
            }
        }

        if (state === "menu" && foundMove?.color !== playerColor) {
            return;
        }

        try {
            const move = game.move({
                from: moveFrom,
                to: square,
                promotion: "q", // always promote to a queen for example simplicity
            });
            resetHighlights();
            setSquareInCheck(undefined);
            onMove(move);
            if (move === null) {
                return false;
            }
        } catch (error) {
            // illegal move

            if (validFromSquares.includes(square)) {
                setMoveFrom(square);
                setSquareStyles(
                    squareStyling({
                        pieceSquare: square,
                        history: game.history({ verbose: true }),
                    }),
                );
                getMoveOptions(square);
                return;
            }

            setMoveFrom(undefined);
            resetHighlights();
            setSquareInCheck(undefined);
            return;
        }

        setPieceSquare(undefined);
        return true;
    };

    const onPromotionPieceSelect = (piece) => {
        // if no piece passed then user has cancelled dialog, don't make move and reset
        if (piece && promotionMove) {
            game.move({
                from: promotionMove.from,
                to: promotionMove.to,
                promotion: piece[1].toLowerCase() ?? "q",
            });
            resetHighlights();
            setSquareInCheck(undefined);
            onMove(promotionMove.san);
        }

        setPromotionMove(undefined);
        setOptionSquares({});
        setMoveFrom(undefined);
        return true;
    };

    const onSquareRightClick = (square: Square) => {
        const colour = "rgba(0, 0, 255, 0.4)";
        setRightClickedSquares({
            ...rightClickedSquares,
            [square]:
                rightClickedSquares[square] && rightClickedSquares[square].backgroundColor === colour
                    ? undefined
                    : { backgroundColor: colour },
        });
    };
    // setSquareStyles({ [square]: { backgroundColor: 'deepPink' } })

    // Show move options on drag start
    const onPieceDragBegin = () => {
        setSquareInCheck({});
    };

    const { width, height } = useViewportSize();

    if (!width) {
        return;
    }

    const isMobile = width < 1024;

    const canMove = ({ piece }) => {
        if (state === "attempt_finished") {
            return false;
        }
        return piece[0] === playerColor || state === "positions";
    };

    const customArrows: any = arrowToDraw ? [arrowToDraw.from, arrowToDraw.to] : [];

    const takenMaterialDifference = getTakenMaterialDifference(game);

    const renderMaterialDifference = (pieces: Record<MaterialDifferencePiece, number>) => {
        const numPawns = pieces["p"];
        const numKnights = pieces["n"];
        const numBishops = pieces["b"];
        const numRooks = pieces["r"];
        const numQueens = pieces["q"];

        return (
            <>
                {Array.from({ length: numPawns }, (_, index) => (
                    <IconChessFilled
                        key={index}
                        className={cn("inline text-2xl w-5 h-5 text-[#bababa]", {
                            "-ml-2": index > 0,
                        })}
                    />
                ))}
                {Array.from({ length: numKnights }, (_, index) => (
                    <IconChessKnightFilled
                        key={index}
                        className={cn("inline text-2xl w-5 h-5 text-[#bababa]", {
                            "-ml-2": index > 0,
                        })}
                    />
                ))}
                {Array.from({ length: numBishops }, (_, index) => (
                    <IconChessBishopFilled
                        key={index}
                        className={cn("inline text-2xl w-5 h-5 text-[#bababa]", {
                            "-ml-2": index > 0,
                        })}
                    />
                ))}
                {Array.from({ length: numRooks }, (_, index) => (
                    <IconChessRookFilled
                        key={index}
                        className={cn("inline text-2xl w-5 h-5 text-[#bababa]", {
                            "-ml-2": index > 0,
                        })}
                    />
                ))}
                {Array.from({ length: numQueens }, (_, index) => (
                    <IconChessQueenFilled
                        key={index}
                        className={cn("inline text-2xl w-5 h-5 text-[#bababa]", {
                            "-translate-x-2": index > 0,
                        })}
                    />
                ))}

                {pieces["value"] > 0 && (
                    <span className="ml-1 -translate-y-1 text-md font-mono text-[#bababa]">+{pieces["value"]}</span>
                )}
            </>
        );
    };

    return (
        <div className="relative">
            {settings.showCapturedPieces && (
                <div className="mb-2 flex absolute -top-8 left-0">
                    {renderMaterialDifference(
                        playerColor === "w" ? takenMaterialDifference.w : takenMaterialDifference.b,
                    )}
                </div>
            )}
            <ReactChessboard
                id="humanVsHuman"
                ref={chessboardRef}
                boardWidth={Math.min(isMobile ? width : width - SIDE_PANEL_WIDTH, isMobile ? 640 : 800, height * 0.8)}
                position={game.fen()}
                onPieceDrop={onDrop}
                customDarkSquareStyle={{
                    backgroundColor: boardStyle.darkSquareColor,
                }}
                customLightSquareStyle={{
                    backgroundColor: boardStyle.lightSquareColor,
                }}
                onMouseOverSquare={onMouseOverSquare}
                boardOrientation={playerColor === "w" ? "white" : "black"}
                onMouseOutSquare={onMouseOutSquare}
                // boardStyle={{
                //     borderRadius: '5px',
                //     boxShadow: `0 5px 15px rgba(0, 0, 0, 0.5)`,
                // }}
                customSquareStyles={{
                    ...optionSquares,
                    ...squareStyles,
                    ...squareInCheck,
                    ...rightClickedSquares,
                    ...lastMoveSquares,
                }}
                dropSquareStyle={dropSquareStyle}
                // onDragOverSquare={onDragOverSquare}
                onSquareClick={onSquareClick}
                onSquareRightClick={onSquareRightClick}
                isDraggablePiece={canMove}
                arePremovesAllowed={enablePremoves && state !== "attempt_finished" && _.isEmpty(arrowToDraw)}
                onPieceDragBegin={onPieceDragBegin}
                promotionDialogVariant="vertical"
                // showPromotionDialog={false}
                animationDuration={game.turn() !== playerColor ? 100 : 200}
                // animationDuration={300}
                onPromotionPieceSelect={promotionMove ? onPromotionPieceSelect : undefined}
                showPromotionDialog={!!promotionMove}
                promotionToSquare={promotionMove?.to}
                customArrows={[customArrows]}
                customArrowColor={boardStyle.arrowColor}
            />
            {/* {isInPlayingState(state) && (mode === "ranked" || mode === "rated") && (
                // <div className="w-72 mx-10 md:w-96 md:mx-20 mt-10">
                <div className="absolute hidden md:block -bottom-20 ">
                    <Attempts />
                </div>
            )} */}
            {settings.showCapturedPieces && (
                <div className="mt-2 flex absolute -bottom-8 left-0">
                    {renderMaterialDifference(
                        playerColor === "w" ? takenMaterialDifference.b : takenMaterialDifference.w,
                    )}
                </div>
            )}
        </div>
    );
};

export default Chessboard;

const squareStyling = ({
    pieceSquare,
    history,
    SQUARE_COLOR_HOVER,
}: {
    pieceSquare: Square;
    history: Move[];
    SQUARE_COLOR_HOVER: any;
}): any => {
    const sourceSquare = history.length && history[history.length - 1].from;
    const targetSquare = history.length && history[history.length - 1].to;

    return {
        [pieceSquare]: { backgroundColor: SQUARE_COLOR_HOVER },
    };
};
